/**
 * Consimțământ cookie-uri.
 *
 * Regula pe care o impune: nimic din categoria „analitice" nu pornește înainte
 * ca utilizatorul să aleagă explicit. Până atunci PostHog nu se încarcă deloc,
 * nu doar că nu trimite evenimente.
 */

export type ConsentCategories = {
  /** Necesare pentru funcționare. Mereu active, nu se pot refuza. */
  essential: true;
  analytics: boolean;
};

export type StoredConsent = ConsentCategories & {
  /** Versiunea politicii la care s-a dat acordul */
  version: number;
  decidedAt: number;
};

/** Se incrementează când se schimbă categoriile, ca să recerem acordul. */
export const CONSENT_VERSION = 1;

const STORAGE_KEY = "suuper_cookie_consent";

const listeners = new Set<(consent: StoredConsent | null) => void>();

export function readConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredConsent;
    // Acordul dat pentru o versiune veche a categoriilor nu mai e valabil
    if (parsed.version !== CONSENT_VERSION) return null;

    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(analytics: boolean): StoredConsent {
  const consent: StoredConsent = {
    essential: true,
    analytics,
    version: CONSENT_VERSION,
    decidedAt: Date.now(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Modul privat sau storage plin: respectăm alegerea doar pe sesiunea curentă
  }

  listeners.forEach((listener) => listener(consent));
  return consent;
}

/** Șterge alegerea, ca bannerul să reapară (folosit de linkul din footer). */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorăm
  }
  listeners.forEach((listener) => listener(null));
}

export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}

export function onConsentChange(
  listener: (consent: StoredConsent | null) => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ============================================
// REDESCHIDEREA PREFERINȚELOR
// ============================================
// Linkul din subsol redeschide panoul cu alegerile curente, fără să le șteargă.

const openRequests = new Set<() => void>();

export function openCookiePreferences(): void {
  openRequests.forEach((handler) => handler());
}

export function onOpenPreferences(handler: () => void): () => void {
  openRequests.add(handler);
  return () => openRequests.delete(handler);
}
